import type {
  ControlCommand,
  ParsedScript,
  PrompterSettings,
  SessionState,
} from "../types";
import { defaultSettings } from "../defaults";
import { validVisualCue } from "../../server/protocol.mjs";

const bounds: Partial<Record<keyof PrompterSettings, [number, number]>> = {
  outputWidth: [320, 7680],
  outputHeight: [240, 4320],
  fontSize: [8, 300],
  lineHeight: [0.5, 5],
  margin: [0, 49],
  readingLine: [0, 100],
  guideOpacity: [0, 1],
  cueOpacity: [0, 1],
  wpm: [10, 600],
  speedStep: [1, 100],
  duration: [1, 86400],
  countdown: [0, 60],
  confidenceThreshold: [0, 1],
  manualOverrideMs: [0, 600000],
};
const choices: Partial<Record<keyof PrompterSettings, readonly string[]>> = {
  alignment: ["left", "center", "right", "justify"],
  direction: ["ltr", "rtl", "auto"],
  mode: ["manual", "constant", "timed", "voice", "sound"],
  speechEngine: ["browser", "local", "streaming", "external"],
  streamingModel: ["small", "tiny"],
  theme: ["dark", "light"],
  layout: ["studio", "focus", "compact"],
  cueLayer: ["front", "behind"],
};
/** Only known, correctly typed settings enter persistent or live session state. */
export function normalizeSettings(
  value: unknown,
  base: PrompterSettings = defaultSettings,
): PrompterSettings {
  const next = { ...base };
  if (!value || typeof value !== "object" || Array.isArray(value)) return next;
  const object = value as Record<string, unknown>;
  for (const key of Object.keys(
    defaultSettings,
  ) as (keyof PrompterSettings)[]) {
    const v = object[key];
    if (v === undefined) continue;
    const range = bounds[key],
      options = choices[key];
    if (range) {
      if (typeof v === "number" && Number.isFinite(v))
        (next as unknown as Record<string, unknown>)[key] = Math.max(
          range[0],
          Math.min(range[1], key === "speedStep" ? Math.round(v) : v),
        );
    } else if (options) {
      if (typeof v === "string" && options.includes(v))
        (next as unknown as Record<string, unknown>)[key] = v;
    } else if (typeof base[key] === "boolean") {
      if (typeof v === "boolean")
        (next as unknown as Record<string, unknown>)[key] = v;
    } else if (
      typeof v === "string" &&
      v.length <= (key === "fontFamily" ? 160 : 80)
    ) {
      if (key === "background" || key === "foreground") {
        if (/^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v))
          (next as unknown as Record<string, unknown>)[key] = v;
      } else (next as unknown as Record<string, unknown>)[key] = v;
    }
  }
  return next;
}
export const clampPosition = (value: number, length: number) =>
  Math.max(
    0,
    Math.min(
      Math.max(0, length - 1),
      Math.round(Number.isFinite(value) ? value : 0),
    ),
  );
export function applySessionCommand(
  state: SessionState,
  command: ControlCommand,
  parsed: ParsedScript,
  now = Date.now(),
): SessionState {
  const max = parsed.words.length;
  switch (command.type) {
    case "play":
    case "toggle": {
      if (command.type === "toggle" && (state.playing || state.countdownUntil))
        return { ...state, playing: false, countdownUntil: null };
      if (
        state.playing ||
        state.countdownUntil ||
        !parsed.words.some((w) => !w.cue)
      )
        return state;
      return {
        ...state,
        countdownUntil:
          state.settings.countdown > 0
            ? now + state.settings.countdown * 1000
            : null,
        playing: state.settings.countdown === 0,
        startedAt: state.startedAt ?? now,
      };
    }
    case "pause":
      return { ...state, playing: false, countdownUntil: null };
    case "reset":
      return {
        ...state,
        wordIndex: 0,
        wordProgress: 0,
        playing: false,
        elapsed: 0,
        startedAt: null,
        countdownUntil: null,
        blackout: false,
        emergency: "",
        visualCue: null,
      };
    case "seek":
      return {
        ...state,
        wordIndex: clampPosition(command.wordIndex, max),
        wordProgress: 0,
      };
    case "nudge":
      return {
        ...state,
        wordIndex: clampPosition(state.wordIndex + command.words, max),
        wordProgress: 0,
      };
    case "speed":
      return {
        ...state,
        settings: normalizeSettings({ wpm: command.wpm }, state.settings),
      };
    case "blackout":
      return { ...state, blackout: command.value ?? !state.blackout };
    case "emergency":
      return { ...state, emergency: command.text.slice(0, 500), visualCue: null };
    case "cue": {
      if (command.cue !== null && !validVisualCue(command.cue)) return state;
      return {
        ...state,
        visualCue: command.cue === null ? null : { ...command.cue },
        emergency: "",
        ...(command.cue && command.pause === true ? { playing: false, countdownUntil: null } : {}),
      };
    }
    case "settings":
      return {
        ...state,
        settings: normalizeSettings(command.settings, state.settings),
      };
  }
}
/** Timer advances spoken words only; stage directions consume no reading time. */
export function advanceSession(
  state: SessionState,
  parsed: ParsedScript,
  dt: number,
  fraction: number,
  speaking: boolean,
  now = Date.now(),
): { state: SessionState; fraction: number } {
  if (state.countdownUntil)
    return {
      state:
        now >= state.countdownUntil
          ? { ...state, countdownUntil: null, playing: true }
          : state,
      fraction: state.wordProgress || 0,
    };
  if (!state.playing) return { state, fraction: state.wordProgress || 0 };
  dt = Math.max(0, Math.min(1, dt));
  const move =
    state.settings.mode === "constant" ||
    state.settings.mode === "timed" ||
    (state.settings.mode === "sound" && speaking);
  const spoken = parsed.words.filter((w) => !w.cue).length;
  let index = state.wordIndex;
  if (move) {
    const rate =
      state.settings.mode === "timed"
        ? spoken / Math.max(1, state.settings.duration)
        : state.settings.wpm / 60;
    fraction += dt * rate;
    const steps = Math.floor(fraction + 1e-9);
    fraction = Math.max(0, fraction - steps);
    for (let n = 0; n < steps; n++) {
      index++;
      while (parsed.words[index]?.cue) index++;
    }
  }
  const end = Math.max(0, parsed.words.length - 1);
  const done = move && index > end;
  return {
    state: {
      ...state,
      wordIndex: clampPosition(index, parsed.words.length),
      wordProgress: done ? 1 : move ? fraction : state.wordProgress,
      elapsed: state.elapsed + dt,
      playing: done ? false : state.playing,
    },
    fraction: done ? 0 : fraction,
  };
}
